import { useCallback } from "react";
import { updatePromoUsed, markPromoRecommendationUsed } from "../../../api";
import {
  buildAllRoomRateplanCombos,
  filterCombosForProperty,
} from "../utils/roomRateCombos";
import { useSubmitDerivedPromo } from "./useSubmitDerivedPromo";
import { useSubmitStandardPromo } from "./useSubmitStandardPromo";

/**
 * CreatePromo submit pipeline orchestrator.
 * Routes between the pilot (derived rates) and standard (savePromo /
 * createSmartPromo) submit branches, and owns the combo-resolution helpers
 * both branches share.
 */
export function useCreatePromoSubmit({
  promo,
  selectedProperty,
  properties,
  isEdit,
  isDuplicate,
  isMarket,
  isPilotHotel,
  rooms,
  ratePlans,
  // Property that `ratePlans`/`rooms` were fetched for — combos built from
  // them are tagged with it so a foreign selection can be filtered out of a
  // property's payload.
  ratePlansPropertyId = null,
  calculationType,
  hideModal,
  setLoading,
  onRecommendationUsed = null,
}) {
  // Combos are tagged with the property the rooms/rate plans were fetched
  // for, so a stale or foreign selection can be detected downstream.
  const buildCombosForPageData = useCallback(
    (roomsData = [], ratePlansData = []) =>
      buildAllRoomRateplanCombos(roomsData, ratePlansData, ratePlansPropertyId),
    [ratePlansPropertyId]
  );

  const getAllowedRateRoomCodes = useCallback(
    (rateRoomCodes) => {
      if (!rateRoomCodes || rateRoomCodes.length === 0) {
        return buildCombosForPageData(rooms, ratePlans);
      }
      return rateRoomCodes;
    },
    [rooms, ratePlans, buildCombosForPageData]
  );

  /**
   * Combos for one property, in order of preference:
   *   1. the current selection, minus anything tagged with another property
   *   2. the selection saved for that property earlier in this session
   *   3. every combo of the page's rooms × rate plans — but only when the page
   *      data belongs to that property (or its owner is unknown, which is the
   *      pre-tagging behaviour). Falling back to another property's data is
   *      what produced promos carrying foreign rateplan ids.
   */
  const getCodesForProperty = useCallback(
    (rateRoomCodes, propertyId) => {
      // Untagged combos came from the page's rooms/rate plans, so they belong
      // to ratePlansPropertyId — not to whichever property is being submitted.
      // Without this they match every property and carry the page property's
      // rateplan ids into another property's payload.
      const own = filterCombosForProperty(
        rateRoomCodes,
        propertyId,
        ratePlansPropertyId
      );
      if (own.length > 0) return own;

      // D8: with isMarket the selection is emptied on purpose and must rebuild
      // to every room × rate plan, so the saved per-property selection is
      // deliberately skipped there.
      if (!isMarket) {
        const saved = filterCombosForProperty(
          promo?.allowedRateRoomCodesByProperty?.[propertyId],
          propertyId
        );
        if (saved.length > 0) return saved;
      }

      const pageDataMatches =
        ratePlansPropertyId == null ||
        propertyId == null ||
        String(ratePlansPropertyId) === String(propertyId);
      return pageDataMatches ? getAllowedRateRoomCodes([]) : [];
    },
    [
      promo?.allowedRateRoomCodesByProperty,
      ratePlansPropertyId,
      isMarket,
      getAllowedRateRoomCodes,
    ]
  );

  // Called after EVERY successful create/update — never on cancel, which reaches
  // hideModal without passing through here.
  const recordRecommendationUsed = useCallback(
    (suggestionPromoId, promoCategory) => {
      // A caller whose recommendation tracks its own state supplies how to record it.
      // Action Centre v2 does, keyed on the card id — which every card has — so this
      // must run regardless of the v1 smart-promo metadata: a card with no v1
      // promo_id, or a theme with no promoCategory, still has to be recorded or it
      // comes back on the next read.
      // Awaited by the caller, so the state has landed before any refetch. Safe to
      // return: this one is written to swallow its own failures.
      if (onRecommendationUsed) {
        return onRecommendationUsed();
      }

      // The v1 endpoints are keyed on the v1 recommendation id, so they apply only to
      // a smart promo. A plain promo carries neither field and must not call them.
      if (!suggestionPromoId || !promoCategory) {
        return undefined;
      }

      // v1 stays fire-and-forget, and its rejection must NOT escape. The promo has
      // already been created by this point; letting a failed mark-used reject into
      // the caller's .then would skip hideModal and surface "Failed to save promo"
      // for a promo that exists — so the user resubmits and duplicates it. The axios
      // interceptor still reports the underlying error.
      const v1Call =
        promoCategory === "alignment"
          ? updatePromoUsed(suggestionPromoId)
          : markPromoRecommendationUsed({
              propertyId: selectedProperty?.propertyId,
              promoIds: [suggestionPromoId],
              type: promoCategory,
            });
      v1Call?.catch?.(() => {});
      return undefined;
    },
    [selectedProperty?.propertyId, onRecommendationUsed]
  );

  const { submitDerivedPromo } = useSubmitDerivedPromo({
    promo,
    selectedProperty,
    isEdit,
    isDuplicate,
    isMarket,
    calculationType,
    hideModal,
    setLoading,
    getCodesForProperty,
    recordRecommendationUsed,
  });

  const { submitStandardPromo } = useSubmitStandardPromo({
    promo,
    selectedProperty,
    properties,
    isEdit,
    isDuplicate,
    isMarket,
    calculationType,
    hideModal,
    setLoading,
    getCodesForProperty,
    recordRecommendationUsed,
  });

  const submitPromo = useCallback(
    (isDraft = false) => {
      if (isPilotHotel) {
        return submitDerivedPromo(isDraft);
      }
      return submitStandardPromo(isDraft);
    },
    [isPilotHotel, submitDerivedPromo, submitStandardPromo]
  );

  return { submitPromo };
}
